import React, { useRef, useState } from 'react';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import { InputGroup, InputGroupAddon } from 'reactstrap';
import { WireTransferGroup } from '../../../models/FlowOfFunds/flow-of-funds.model';
import { formatDate } from '../../../utils/flowOfFundsFormat';
import { ConfirmToggleButton } from './ConfirmToggleButton';
import { ConfirmationMeta } from './ConfirmationMeta';
import { DateInput } from './DateInput';

export interface SentConfirmationControlProps {
    group: WireTransferGroup;
    isPending: boolean;
    onConfirm: (paymentDate: Date) => void;
    onUndo: () => void;
}

export const SentConfirmationControl = ({
    group,
    isPending,
    onConfirm,
    onUndo,
}: SentConfirmationControlProps) => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const datePickerRef = useRef<any>(null);

    if (!group.canConfirmSent) {
        return (
            <>
                <span className="ff-wires__date">
                    Payment Date <strong>{formatDate(group.paymentDate)}</strong>
                </span>
                <span
                    className={`ff-wires__status ${
                        group.sentConfirmation
                            ? 'ff-wires__status--sent'
                            : 'ff-wires__status--pending'
                    }`}>
                    {group.sentConfirmation ? 'Payment Sent' : 'Payment Not Sent'}
                </span>
                <ConfirmationMeta confirmation={group.sentConfirmation} />
            </>
        );
    }

    if (group.sentConfirmation) {
        const canUndo = !group.receiptConfirmation;
        return (
            <>
                <span className="ff-wires__date">
                    Payment Date <strong>{formatDate(group.paymentDate)}</strong>
                </span>
                <ConfirmToggleButton
                    label="Confirm Sent"
                    pendingLabel="Undoing…"
                    isOn
                    isPending={isPending}
                    onClick={canUndo ? onUndo : undefined}
                />
                <ConfirmationMeta confirmation={group.sentConfirmation} />
            </>
        );
    }

    return (
        <>
            <span className="ff-wires__date">
                Payment Date
                <InputGroup className="ff-wires__date-picker">
                    <InputGroupAddon
                        addonType="prepend"
                        onClick={() => datePickerRef.current?.setOpen(true)}>
                        <div className="input-group-text">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                        </div>
                    </InputGroupAddon>
                    <DatePicker
                        ref={datePickerRef}
                        selected={selectedDate}
                        onChange={(value: Date) => setSelectedDate(value)}
                        dateFormat="MM/dd/yyyy"
                        className="form-control"
                        popperProps={{ strategy: 'fixed' }}
                        customInput={<DateInput />}
                    />
                </InputGroup>
            </span>
            <ConfirmToggleButton
                label="Confirm Sent"
                pendingLabel="Confirming…"
                isOn={false}
                isPending={isPending}
                disabled={!selectedDate}
                onClick={() => selectedDate && onConfirm(selectedDate)}
            />
        </>
    );
};
